import React from 'react'

import { UserDisplay } from '@/components/UserDisplay/UserDisplay'

import { EntityAuditLogEntry } from '@/schema'
import { getAvatarColor } from '@/utils/avatar'
import { cn } from '@/utils/common'

import { HistoryAuditMessage } from './HistoryAuditMessage'
import { getAbsoluteTime, getLogTypeIcon, getRelativeTime } from '../utils'

export interface HistoryAuditRowProps {
  entry: EntityAuditLogEntry
  /** Pass isToday=true so time column shows relative time */
  isToday: boolean
}

const HistoryAuditRow: React.FC<HistoryAuditRowProps> = ({
  entry,
  isToday,
}) => {
  const { user, created_at, log_type } = entry

  const displayTime = isToday
    ? getRelativeTime(created_at)
    : getAbsoluteTime(created_at)

  const LogIcon = getLogTypeIcon(log_type)
  const ringBg = user.avatar_url ? 'bg-gray-200' : getAvatarColor(user.id).bg

  return (
    <div
      className={cn(
        'flex gap-3 py-3 px-1 rounded-lg transition-colors',
        'hover:bg-blue-50/40'
      )}
    >
      {/* Avatar with action-type icon badge */}
      <div className="relative flex-shrink-0 w-10 h-10">
        <div
          className={cn('absolute -inset-0.5 rounded-full opacity-40', ringBg)}
        />
        <UserDisplay
          user={user}
          showName={false}
          avatarSize="size-10"
          className="relative z-10 !gap-0"
          avatarBadge={
            <span className="absolute -bottom-1 -right-1 z-20 flex items-center justify-center w-5 h-5 bg-gray-100 rounded-full border-solid border-white border-2">
              <LogIcon size={10} className="text-gray-600" />
            </span>
          }
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pt-0.5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <UserDisplay
              user={user}
              showAvatar={false}
              className="!gap-0"
              textClassName="block text-sm font-semibold text-primary-900 leading-snug"
            />
            <p className="mt-0.5 text-sm font-normal text-tertiary-600 leading-snug m-0">
              <HistoryAuditMessage entry={entry} />
            </p>
          </div>
          <span className="text-xs text-secondary-400 flex-shrink-0 whitespace-nowrap pt-0.5">
            {displayTime}
          </span>
        </div>
      </div>
    </div>
  )
}

export default HistoryAuditRow
